import type {
  AddPlayer,
  AddPlayers,
  BoardBuilderDurationStatProjections,
  BoardBuilderDurationStatTypes,
  BoardBuilderProjection,
  BoardBuilderStatProjections
} from '@library/types/boardBuilder';
import type {
  BaseLeagueFilters,
  LeagueInfo,
  LeagueProjectionAttributes
} from '@library/types/leagues';
import { keys, pluck } from 'ramda';
import type { FormattedGameMode } from '@library/types/gameModes';
import type { Updater } from 'use-immer';
import { current } from 'immer';
import { defaultTradeSettingAttributes } from '@bo-constants/tradeSettings';
import { getCalculatedBoardStartTimes } from '@bo-utils/leagueTimes';
import { getLeagueDurationAttributes } from '@bo-utils/leagues';
import { isLeagueFullDuration } from '@bo-utils/leagueProjectionFilters';
import { v4 as uuid } from 'uuid';

export const isStatUnavailableInDuration = ({
  statType,
  leagueDurationAttributes
}: {
  statType: LeagueProjectionAttributes;
  leagueDurationAttributes: BaseLeagueFilters;
}) => {
  const leagueDurationStatTypesIds = pluck(
    'leagueProjectionFilterId',
    leagueDurationAttributes.statTypes
  );
  return !leagueDurationStatTypesIds.includes(
    statType.leagueProjectionFilterId
  );
};

const generatePlayerProjection = ({
  durationId,
  durations,
  gameModes,
  gamePlayer,
  leagueDurationAttributes,
  mainDurationId,
  statType
}: {
  durationId: string;
  durations: Array<LeagueProjectionAttributes>;
  gameModes: Array<FormattedGameMode>;
  gamePlayer: AddPlayer;
  leagueDurationAttributes: BaseLeagueFilters;
  mainDurationId: string;
  statType: LeagueProjectionAttributes;
}): BoardBuilderProjection => {
  const { game, player } = gamePlayer;
  const { foreign_id: foreignId, opponent_team: opponentTeam } = player;

  const { leagueId, name: leagueName } = leagueDurationAttributes;

  const {
    maxProbabilityOfMore,
    minProbabilityOfMore,
    targetProbabilityOfMore
  } = defaultTradeSettingAttributes.standard;

  const { boardTime, startTime } = getCalculatedBoardStartTimes({
    durationId,
    durations,
    gameStartTime: player.start_time || game.date_time,
    leagueName
  });

  return {
    averageScore: undefined,
    autoTrade: true,
    boardTime,
    description: opponentTeam || game?.display || '',
    discountPercentage: '',
    hasSuggestedLines: false,
    inGame: false,
    isPromo: false,
    rank: 0,
    startTime,
    displayTime: startTime,
    projectionId: uuid(),
    projectionValue: '',
    durationId,
    gameId: game?.game_id || null,
    gameModeIds: gameModes?.map(({ id }) => id) ?? [],
    leagueId,
    oddsType: 'standard',
    player: {
      ...player,
      id: '',
      player_id: '',
      league_id: Number(leagueId),
      team_name: player.team_name || player.team
    },
    playerForeignId: foreignId,
    rowStatus: 'NEW',
    statType: statType.leagueProjectionFilterName,
    statTypeId: statType.leagueProjectionFilterId,
    status: mainDurationId === durationId ? 'pre_game' : 'removed',
    maxProbabilityOfMore,
    minProbabilityOfMore,
    targetProbabilityOfMore
  };
};

const generateStatPlayerProjections = ({
  durationId,
  durations,
  gameModes,
  gamePlayers,
  leagueDurationAttributes,
  mainDurationId,
  statType
}: {
  durationId: string;
  durations: Array<LeagueProjectionAttributes>;
  gameModes: Array<FormattedGameMode>;
  gamePlayers: AddPlayers;
  leagueDurationAttributes: BaseLeagueFilters;
  mainDurationId: string;
  statType: LeagueProjectionAttributes;
}): BoardBuilderStatProjections => {
  const statProjections = gamePlayers.map((gamePlayer) =>
    generatePlayerProjection({
      durationId,
      durations,
      gameModes,
      gamePlayer,
      mainDurationId,
      leagueDurationAttributes,
      statType
    })
  );

  return {
    projections: statProjections
  };
};

const generateDurationStatPlayerProjections = ({
  durationId,
  durations,
  gameModes,
  gamePlayers,
  leagueDurationAttributes,
  mainDurationId,
  statTypes
}: {
  durationId: string;
  durations: Array<LeagueProjectionAttributes>;
  gameModes: Array<FormattedGameMode>;
  gamePlayers: AddPlayers;
  leagueDurationAttributes: BaseLeagueFilters;
  leagueInfo: LeagueInfo;
  mainDurationId: string;
  statTypes: Array<LeagueProjectionAttributes>;
}): BoardBuilderDurationStatTypes => {
  const eligibleStatTypes = statTypes.filter(
    (statType) =>
      !isStatUnavailableInDuration({ statType, leagueDurationAttributes })
  );

  const durationStatTypeProjections: BoardBuilderDurationStatTypes = {
    statTypes: {}
  };

  eligibleStatTypes.forEach((statType) => {
    const statTypeProjections = generateStatPlayerProjections({
      durationId,
      durations,
      gameModes,
      gamePlayers,
      leagueDurationAttributes,
      mainDurationId,
      statType
    });

    durationStatTypeProjections.statTypes[statType.leagueProjectionFilterId] =
      statTypeProjections;
  });

  return durationStatTypeProjections;
};

export const processDurationStatProjections = ({
  confirmedGameModes,
  confirmedPlayersList,
  confirmedStatTypesList,
  durations,
  leagueInfo,
  mainDurationId,
  setDurationStatProjections,
  statTypes
}: {
  confirmedGameModes: Array<FormattedGameMode>;
  confirmedPlayersList: AddPlayers;
  confirmedStatTypesList: Array<string>;
  durations: Array<LeagueProjectionAttributes>;
  leagueInfo: LeagueInfo;
  mainDurationId: string;
  statTypes: Array<LeagueProjectionAttributes>;
  setDurationStatProjections: Updater<BoardBuilderDurationStatProjections>;
}) => {
  const confirmedStatTypes = statTypes.filter((statType) =>
    confirmedStatTypesList.includes(statType.leagueProjectionFilterId)
  );

  setDurationStatProjections((draft) => {
    // Process projections for each duration

    durations.forEach((duration) => {
      const {
        leagueProjectionFilterId: durationId,
        leagueProjectionFilterName
      } = duration;

      const leagueDurationAttributes = getLeagueDurationAttributes({
        durationId,
        leagueInfo
      });

      const isFullDuration = isLeagueFullDuration(leagueProjectionFilterName);

      const filteredConfirmedGameModes = confirmedGameModes.filter(
        ({ name }) => isFullDuration || !name.includes('squads')
      );

      if (!draft[durationId]) {
        // Create all Projections for the selected duration
        const generatedDurationStatProjections =
          generateDurationStatPlayerProjections({
            durationId,
            durations,
            gameModes: filteredConfirmedGameModes,
            gamePlayers: confirmedPlayersList,
            leagueDurationAttributes,
            leagueInfo,
            mainDurationId,
            statTypes: confirmedStatTypes
          });

        draft[durationId] = generatedDurationStatProjections;
        return;
      }

      // Delete Stat types and projections from draft for the unselected Stat Types
      const durationsStatTypes = draft[durationId].statTypes;
      const durationStatTypesList = keys(durationsStatTypes);

      durationStatTypesList.forEach((durationStatTypeId) => {
        if (!confirmedStatTypesList.includes(durationStatTypeId)) {
          delete durationsStatTypes[durationStatTypeId];
        }
      });

      // Process projections for each selected stat type
      confirmedStatTypes.forEach((confirmedStatType) => {
        if (
          isStatUnavailableInDuration({
            statType: confirmedStatType,
            leagueDurationAttributes
          })
        ) {
          return;
        }

        // If draftDurationStatType does not exist for this confirmed stat type,
        // create confirmed stat and projection for each confirmed player
        const confirmedStatTypeId = confirmedStatType.leagueProjectionFilterId;

        if (!durationsStatTypes[confirmedStatTypeId]) {
          const generatedStatPlayerProjections = generateStatPlayerProjections({
            durationId,
            durations,
            gameModes: filteredConfirmedGameModes,
            gamePlayers: confirmedPlayersList,
            leagueDurationAttributes,
            mainDurationId,
            statType: confirmedStatType
          });

          durationsStatTypes[confirmedStatTypeId] =
            generatedStatPlayerProjections;
          return;
        }

        // If draftDurationStatType exists for this confirmed stat type,
        const draftStatProjections =
          durationsStatTypes[confirmedStatTypeId].projections;

        // Delete stat projections for game players that doesn't exist in the confirmedPlayersList due to player unselect

        // If all the players are removed clear the projections
        if (!confirmedPlayersList.length) {
          draftStatProjections.length = 0;
        }
        const copyDraftStatProjections = current(draftStatProjections);

        copyDraftStatProjections.forEach((projection) => {
          const isExistsInConfirmedPlayersList = confirmedPlayersList.some(
            (confirmedPlayer) =>
              confirmedPlayer.player.foreign_id ===
                projection.playerForeignId &&
              confirmedPlayer.game.game_id === projection.gameId
          );

          if (!isExistsInConfirmedPlayersList) {
            const projectionIndex = draftStatProjections.findIndex(
              (indexedProjection) =>
                projection.projectionId === indexedProjection.projectionId
            );
            if (projectionIndex !== -1) {
              draftStatProjections.splice(projectionIndex, 1);
            }
          }
        });

        //  create stat projections for each newly confirmed player
        confirmedPlayersList.forEach((confirmedPlayer) => {
          const {
            game: { game_id: gameId },
            player: { foreign_id: foreignId }
          } = confirmedPlayer;

          const draftStatProjectionIndex = draftStatProjections.findIndex(
            (draftStatProjection) =>
              draftStatProjection.gameId === gameId &&
              draftStatProjection.playerForeignId === foreignId &&
              draftStatProjection.statTypeId ===
                confirmedStatType.leagueProjectionFilterId
          );

          // If draft projection doesn't exist for this confirmed player create draft projection
          if (draftStatProjectionIndex === -1) {
            const generatedPlayerProjection = generatePlayerProjection({
              durationId,
              durations,
              gameModes: filteredConfirmedGameModes,
              gamePlayer: confirmedPlayer,
              leagueDurationAttributes,
              mainDurationId,
              statType: confirmedStatType
            });
            draftStatProjections.push(generatedPlayerProjection);
          }
        });
      });
    });
  });
};
